import {gql} from '@apollo/client';
import assert from 'assert';
import React from 'react';

import {FormDefinitionQuery} from 'forms/form-definition/__generated/FormDefinitionQuery';
import {
  DropdownInfoQuery,
  DropdownInfoQueryVariables,
} from 'forms/form-designer/__generated/DropdownInfoQuery';
import {
  TagSchemaInfoQuery,
  TagSchemaInfoQueryVariables,
} from 'forms/form-designer/__generated/TagSchemaInfoQuery';
import {
  BNCH_TYPE_DEF_TO_FORM_FIELD_TYPE,
  FormDefinitionField,
  FormFieldType,
  formFieldTypeToReadableName,
} from 'forms/form-designer/Constants';
import {FormActionForSchema} from 'forms/form-designer/graphql/__generated/FormActionForSchema';
import {FormFixAnalytics} from 'forms/FormsAnalytics';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getTagSchemaSettingsUrl, getSchemaFieldSelectorSettingsUrl} from 'registry/RegistryUtil';
import {FIELD_TYPE_TO_LEAF_TYPE, FieldType} from 'schemas/fields/SchemaFieldConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';

type FormActionInvocationParameter = ExtractByTypename<
  FormActionForSchema['formAction'],
  'FormActionNode'
>['invocationParameters'][number];

type FormFieldToActionParam = ExtractByTypename<
  FormDefinitionQuery['formDefinition'],
  'FormDefinitionNode'
>['latestFormDefinitionVersion']['actionConfigs'][number]['parameters'][number];

export const TAG_SCHEMA_INFO_QUERY = gql`
  query TagSchemaInfoQuery($id: StrictString!) @skipAudit @engComponent(name: FORMS) {
    tagSchema(id: $id) {
      ... on TagSchemaNode {
        id
        name
        registry {
          id
          organization {
            id
            urlIdentifier
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const DROPDOWN_INFO_QUERY = gql`
  query DropdownInfoQuery($id: StrictString!) @skipAudit @engComponent(name: FORMS) {
    schemaFieldSelector(id: $id) {
      ... on SchemaFieldSelectorNode {
        id
        name
        registry {
          id
          organization {
            id
            urlIdentifier
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function convertTagSchemaIdToLink(tagSchemaId?: string | null): JSX.Element | null {
  if (!tagSchemaId) {
    return null;
  }
  const fallback = <span>{tagSchemaId}</span>;
  return (
    <BenchlingQuery<TagSchemaInfoQuery, TagSchemaInfoQueryVariables>
      query={TAG_SCHEMA_INFO_QUERY}
      renderLoading={() => fallback}
      variables={{id: tagSchemaId}}
      fetchPolicy="cache-first"
    >
      {({tagSchema}) => {
        if (isInaccessible(tagSchema)) {
          return fallback;
        }
        const href = getTagSchemaSettingsUrl(
          tagSchema.registry.organization.urlIdentifier,
          tagSchema.registry.id,
          tagSchemaId
        );
        return <a href={href}>{tagSchema.name}</a>;
      }}
    </BenchlingQuery>
  );
}

function convertDropdownIdToLink(dropdownId?: string | null): JSX.Element | null {
  if (!dropdownId) {
    return null;
  }
  const fallback = <span>{dropdownId} (dropdown)</span>;
  return (
    <BenchlingQuery<DropdownInfoQuery, DropdownInfoQueryVariables>
      query={DROPDOWN_INFO_QUERY}
      renderLoading={() => fallback}
      variables={{id: dropdownId}}
      fetchPolicy="cache-first"
    >
      {({schemaFieldSelector}) => {
        if (isInaccessible(schemaFieldSelector)) {
          return fallback;
        }

        const href = getSchemaFieldSelectorSettingsUrl(
          schemaFieldSelector.registry.organization.urlIdentifier,
          schemaFieldSelector.registry.id,
          dropdownId
        );
        return (
          <>
            <a href={href}>{schemaFieldSelector.name}</a>
            {' (dropdown)'}
          </>
        );
      }}
    </BenchlingQuery>
  );
}

function fieldTypeToFormFieldType(type: FieldType): FormFieldType {
  return BNCH_TYPE_DEF_TO_FORM_FIELD_TYPE[FIELD_TYPE_TO_LEAF_TYPE[type]] ?? FormFieldType.BOOLEAN;
}

/**
 * Represents a single autofix update to a form field.
 */
export class FormFixUpdate {
  updateType: FormFixAnalytics;
  field: FormDefinitionField | null | undefined;
  invocationParam: FormActionInvocationParameter | null;

  constructor(
    updateType: FormFixAnalytics,
    field: FormDefinitionField | null | undefined,
    invocationParam: FormActionInvocationParameter | null
  ) {
    this.updateType = updateType;
    this.field = field;
    this.invocationParam = invocationParam;
  }

  /**
   * @returns a string representation of this update for analytics.
   */
  toAnalyticsString(): FormFixAnalytics {
    return this.updateType;
  }

  /**
   * @returns a user-facing autofix message as a JSX element.
   */
  toElement(): JSX.Element {
    let fieldName: string = this.field?.label ?? this.invocationParam!.name;
    if (this.field?.matchesSchemaFieldName && this.invocationParam) {
      fieldName = this.invocationParam.name;
    }

    switch (this.updateType) {
      case FormFixAnalytics.ADDED_FIELD:
        return (
          <span>
            <strong>{fieldName}</strong>
            {this.invocationParam!.isRequired ? '' : ' (optional)'} was added.
          </span>
        );
      case FormFixAnalytics.ARCHIVED_FIELD:
        return (
          <span>
            <strong>{fieldName}</strong> was archived.
          </span>
        );
      case FormFixAnalytics.RENAMED_FIELD:
        return (
          <span>
            <strong>{fieldName}</strong> was renamed from {this.field!.label}.
          </span>
        );
      case FormFixAnalytics.CHANGED_FIELD_REQUIRED: {
        // Form field is added if it was not included in the original definition
        const optionalText = this.field ? '' : ' and added to the form';
        return (
          <span>
            <strong>{fieldName}</strong> was made required{optionalText}.
          </span>
        );
      }
      case FormFixAnalytics.CHANGED_FIELD_TYPE: {
        assert(this.field && this.invocationParam);
        const oldTypeFormatted =
          convertTagSchemaIdToLink(this.field.schemaId) ??
          convertDropdownIdToLink(this.field.schemaFieldSelectorId) ??
          formFieldTypeToReadableName(this.field.fieldType);
        const newTypeFormatted =
          convertTagSchemaIdToLink(this.invocationParam.linkedSchemaId) ??
          convertDropdownIdToLink(this.invocationParam.schemaFieldSelectorId) ??
          formFieldTypeToReadableName(
            fieldTypeToFormFieldType(this.invocationParam.parameterTypeName as FieldType)
          );

        return (
          <span>
            <strong>{fieldName}</strong> type changed from {oldTypeFormatted} to {newTypeFormatted}.
          </span>
        );
      }
      default:
        return (
          <span>
            <strong>{fieldName}</strong> was updated.
          </span>
        );
    }
  }
}

/**
 * Returns a user-facing list of "diff" messages required to fix the form definition field.
 * The diff messages are JSX elements because they contain bolded text and links.
 *
 * @param field The old form definition field if existing, null if field was not included, undefined if field is new
 * @param invocationParam The new action config parameter associated with the form field, null if field is now archived
 * @returns List of changes to make the old field match the new action parameter.
 */
function calculateFieldDiffs(
  field: FormDefinitionField | null | undefined,
  invocationParam: FormActionInvocationParameter | null
): Array<FormFixUpdate> {
  if (!invocationParam) {
    assert(field !== undefined, 'Invocation param and field cannot be null and undefined');
    if (field == null) {
      // If field was not included and the schema field was archived, there is no change to the form.
      return [];
    }
    return [new FormFixUpdate(FormFixAnalytics.ARCHIVED_FIELD, field, invocationParam)];
  }

  if (field === undefined) {
    return [new FormFixUpdate(FormFixAnalytics.ADDED_FIELD, field, invocationParam)];
  }

  const updates: Array<FormFixUpdate> = [];

  if (field?.matchesSchemaFieldName && field.label !== invocationParam.name) {
    updates.push(new FormFixUpdate(FormFixAnalytics.RENAMED_FIELD, field, invocationParam));
  }

  if (invocationParam.isRequired && !field?.isRequired) {
    updates.push(new FormFixUpdate(FormFixAnalytics.CHANGED_FIELD_REQUIRED, field, invocationParam));
  }

  // Check for field type changes only if the original field existed
  if (field) {
    const oldFieldType = field.schemaId ?? field.schemaFieldSelectorId ?? field.fieldType;
    const newFieldType =
      invocationParam.linkedSchemaId ??
      invocationParam.schemaFieldSelectorId ??
      fieldTypeToFormFieldType(invocationParam.parameterTypeName as FieldType);
    if (oldFieldType !== newFieldType) {
      updates.push(new FormFixUpdate(FormFixAnalytics.CHANGED_FIELD_TYPE, field, invocationParam));
    }
  }

  return updates;
}

export function getFormDefinitionUpdates(
  fields: Array<FormDefinitionField>,
  formFieldToActionParams: Array<FormFieldToActionParam>,
  invocationParams: Array<FormActionInvocationParameter>
): Array<FormFixUpdate> {
  const fieldKeyToField = new Map(fields.map((f) => [f.key, f]));

  // Existing form action config to form fields. If a schema field is not included in the form, the value in this map is null.
  const actionParamIdToField = new Map<string, FormDefinitionField | null>(
    formFieldToActionParams.map((p) => [
      p.actionParameterId,
      p.formFieldKey ? fieldKeyToField.get(p.formFieldKey)! : null,
    ])
  );
  const actionParamIdToActionParam = new Map<string, FormActionInvocationParameter>(
    invocationParams.map((p) => [p.id, p])
  );

  const updates: Array<FormFixUpdate> = [];

  // Calculate field diffs for changed form fields and removed form fields
  for (const [actionParamId, actionParam] of actionParamIdToActionParam.entries()) {
    const field = actionParamIdToField.get(actionParamId);
    updates.push(...calculateFieldDiffs(field, actionParam));
    actionParamIdToField.delete(actionParamId);
  }

  // Calculate field diffs for added form fields
  for (const [actionParamId, field] of actionParamIdToField.entries()) {
    const actionParam = actionParamIdToActionParam.get(actionParamId) ?? null;
    if (field == null) {
      // Old schema had an optional schema field X that the form creator chose not to include. The schema was updated to remove field X.
      continue;
    }
    updates.push(...calculateFieldDiffs(field, actionParam));
  }

  return updates;
}

export default function FormFixBanner({
  taskSchemaName,
  taskSchemaURL,
  updates,
  onDismiss,
}: {
  taskSchemaName: string;
  taskSchemaURL: string;
  updates: Array<FormFixUpdate>;
  onDismiss: () => void;
}): JSX.Element | null {
  const pluralizedChangesText = updates.length === 1 ? 'this change' : 'these changes';

  const text = updates.length ? (
    <span>
      Changes have been applied to this form due to an update to <a href={taskSchemaURL}>{taskSchemaName}</a>.
      Save the form to apply {pluralizedChangesText} and make the form valid:
    </span>
  ) : (
    <span>
      This form was marked invalid due to an update to <a href={taskSchemaURL}>{taskSchemaName}</a>. Save the
      form to acknowledge the schema update and mark the form valid.
    </span>
  );

  const supportingInfo = updates.length ? (
    <ul className="u-no-padding u-no-spacing">
      {updates.map((update, i) => (
        <li key={i}>{update.toElement()}</li>
      ))}
    </ul>
  ) : undefined;

  return (
    <div className="u-spacing-bottom">
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text={text}
        supportingInfo={supportingInfo}
        onDismiss={onDismiss}
      />
    </div>
  );
}
